import React, { useEffect, useState } from 'react';
import { additionalLearningTypeIcons } from '../additional-learning/additional-learning.models';
import { CompletionStatus } from '../../../enums';
import { ExtentAddressed } from '../../../enums/extent-addressed';
import { FetchType } from '../../../enums/fetch';
import { Reflection } from '../../../models/view/reflection';
import { useAppActions } from '../../../overmind';
import { useForm } from 'react-hook-form';
import { PagePath } from '../../../navigation/navigation.enums';
import { FocusPanelLoaderLayer } from '../../../components/general/loading-state/loader-layers/focus-panel-loader-layer/focus-panel-loader-layer';
import { Anchor, Button, PanelHeader, IPillButton, ListItemLayout, ListLayout, PillButtonGroup, RadioButton, RequiredValidator, useMatchScreenWidth, TextArea, FieldController, DescriptionValidator, FormGroup } from '@keplerco/core';

export function ReflectionPanel(props: {
  reflectionId: number | undefined;
  path: PagePath.learningJourneyCourseReflections | PagePath.learningJourneyAdditionalLearning | PagePath.learningJourneySkill | PagePath.dashboard | PagePath.learningJourneyYearSkill | PagePath.learningJourneyAssignedSkill;
  onCancel?: () => void;
  onSubmit: () => void;
}): JSX.Element {
  const actions = useAppActions();

  const [reflection, setReflection] = useState<Reflection>();

  const [pillButtons, setPillButtons] = useState<IPillButton[]>([
    { id: ExtentAddressed.NotAtAll, label: 'Not At All' },
    { id: ExtentAddressed.Somewhat, label: 'Somewhat' },
    { id: ExtentAddressed.Partially, label: 'Partially' },
    { id: ExtentAddressed.Completely, label: 'Completely' },
  ]);

  const { control, setValue, handleSubmit, trigger } = useForm({ mode: 'onBlur', reValidateMode: 'onBlur' });

  useEffect(() => {
    async function getData() {
      if (!props.reflectionId) return;

      actions.startLoader({ path: props.path, type: FetchType.DialogFetching });

      const data = await actions.getReflection(props.reflectionId);
      setReflection(data);

      const hasBeenCompleted = data?.reflectionCompletionStatus === CompletionStatus.Completed;

      if (hasBeenCompleted) {
        setValue('extentAddressed', data.extentAddressed);
        setValue('description', data.addressedDescription);
        setValue('competencyApplied', data.competencyApplied ? 'Yes' : 'No');

        setPillButtons(currentState => {
          const newState = structuredClone(currentState);
          newState.forEach((pillButton: IPillButton) => (pillButton.active = pillButton.id === data.extentAddressed));
          return newState;
        });
      }

      actions.stopLoader(props.path);
    }

    getData();
  }, [props.reflectionId]);

  const isMobile = useMatchScreenWidth('mobile');

  return (
    <FocusPanelLoaderLayer path={props.path}>
      <div className="panelContent panelForm">
        <PanelHeader title="Reflect on your learning" type="image">
          <div className="headerImage">
            <img src={reflection?.lessonImage || additionalLearningTypeIcons.find(item => item.type === reflection?.offPlatformLearningType)?.panelUrl} alt="" draggable="false" style={{ width: isMobile ? '100%' : 270 }} />

            <h5>{reflection?.lessonName}</h5>
          </div>
        </PanelHeader>

        <form
          id="saveReflection"
          className="panelForm"
          onSubmit={handleSubmit(async fieldValues => {
            actions.startLoader({ path: props.path, type: FetchType.Sending });
            
            const request = {
              ...reflection,
              ...fieldValues,
              competencyApplied: fieldValues.competencyApplied === 'Yes' ? true : false,
            } as Reflection;
            await actions.saveReflection(request);
            props.onSubmit();

            actions.stopLoader(props.path);
          })}
        >
          <FieldController
            control={control}
            name="extentAddressed"
            rules={new RequiredValidator('How well did this activity address your needs?')}
            render={({ fieldState }) => {
              return (
                <FormGroup label="How well did this activity address your learning needs?" validation={{ dirty: fieldState.isDirty || !!fieldState.error, invalid: !!fieldState.error, message: fieldState.error?.message ?? 'How well did this activity address your needs?' }} paddingTop={24}>
                  <PillButtonGroup
                    buttons={pillButtons}
                    backgroundColour="link-text"
                    activeTextColour="background"
                    onClick={buttons => {
                      setValue('extentAddressed', buttons[0].id);
                      trigger('extentAddressed');
                    }}
                  />
                </FormGroup>
              );
            }}
          />

          <FieldController
            control={control}
            name="description"
            rules={new DescriptionValidator('Enter a description')}
            render={({ field, fieldState }) => (
              <FormGroup label="Provide a brief explanation for your rating?" validation={{ dirty: fieldState.isDirty || !!fieldState.error, invalid: !!fieldState.error, message: fieldState.error?.message ?? 'Enter a description' }} paddingTop={24}>
                <TextArea
                  {...field}
                  validation={{
                    dirty: fieldState.isDirty || !!fieldState.error,
                    invalid: !!fieldState.error,
                    message: '',
                  }}
                  label="Description"
                  responsive
                />
              </FormGroup>
            )}
          />

          <FieldController
            control={control}
            name="competencyApplied"
            rules={new RequiredValidator('Have you applied this learning?')}
            render={({ field, fieldState }) => {
              return (
                <FormGroup label="Have you been able to apply what you learned in your work?" validation={{ dirty: fieldState.isDirty || !!fieldState.error, invalid: !!fieldState.error, message: fieldState.error?.message ?? 'Have you applied this learning?' }} paddingTop={24}>
                  <ListLayout wrap gap={20}>
                    <ListItemLayout>
                      <RadioButton {...field} id="Yes" value="Yes" clickablearea="label" checked={field.value === 'Yes'}>
                        Yes
                      </RadioButton>
                    </ListItemLayout>

                    <ListItemLayout>
                      <RadioButton {...field} id="No" value="No" clickablearea="label" checked={field.value === 'No'}>
                        No
                      </RadioButton>
                    </ListItemLayout>
                  </ListLayout>
                </FormGroup>
              );
            }}
          />

          <footer className="panelFooter" style={{ marginTop: 24 }}>
            {!!props.onCancel && <Anchor onClick={props.onCancel}>Cancel</Anchor>}

            <Button type="button">Save reflection</Button>
          </footer>
        </form>
      </div>
    </FocusPanelLoaderLayer>
  );
}
